import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import AuthCard from '../components/auth/AuthCard.tsx';
import OtpInput from '../components/auth/OtpInput.tsx';
import { MFA_EMAIL_STORAGE_KEY, MFA_TOKEN_STORAGE_KEY } from '../services/mfa.ts';

const LoginMfa: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeMfaLogin } = useAuth();

  const state = (location.state || {}) as { email?: string; mfaToken?: string };
  const mfaToken = state.mfaToken || sessionStorage.getItem(MFA_TOKEN_STORAGE_KEY) || '';
  const email = state.email || sessionStorage.getItem(MFA_EMAIL_STORAGE_KEY) || '';

  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const subtitle = useMemo(() => {
    if (useRecovery) {
      return email ? (
        <>
          Digite um código de recuperação de uso único para <strong>{email}</strong>.
        </>
      ) : (
        'Digite um código de recuperação de uso único.'
      );
    }
    return email ? (
      <>
        Abra o aplicativo autenticador e digite o código de 6 dígitos para <strong>{email}</strong>.
      </>
    ) : (
      'Abra o aplicativo autenticador e digite o código de 6 dígitos.'
    );
  }, [email, useRecovery]);

  if (!mfaToken) {
    return (
      <AuthCard
        title="Verificação em duas etapas"
        subtitle="Sessão MFA expirada. Faça login novamente."
        footer={
          <Link to="/login" className="btn btn-primary" style={{ textDecoration: 'none' }}>
            Voltar ao login
          </Link>
        }
      >
        <div />
      </AuthCard>
    );
  }

  const submitCode = async (value: string) => {
    if (!value.trim()) {
      setError('Informe o código para continuar.');
      return;
    }
    try {
      setError('');
      setLoading(true);
      await completeMfaLogin(mfaToken, value.trim());
      navigate('/dashboard');
    } catch (err: unknown) {
      const message =
        (err as Error).message ||
        'Código inválido. O código informado está incorreto ou expirou. Confira o autenticador e tente novamente.';
      setError(message);
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitCode(useRecovery ? recoveryCode : code);
  };

  return (
    <AuthCard
      title="Verificação em duas etapas"
      subtitle={subtitle}
      stepChip="Etapa 2 de 2"
      error={
        error ? (
          <div className="auth-error">
            <strong>Código inválido</strong>
            {error.replace(/^Código inválido\.?\s*/i, '') ||
              'O código informado está incorreto ou expirou. Confira o autenticador e tente novamente.'}
          </div>
        ) : null
      }
      footer={
        <>
          <button type="submit" form="mfa-form" className="btn btn-primary" disabled={loading}>
            {loading ? 'Confirmando…' : 'Confirmar'}
          </button>
          <div className="auth-links">
            <button
              type="button"
              className="auth-link"
              onClick={() => {
                setUseRecovery(v => !v);
                setError('');
                setCode('');
                setRecoveryCode('');
              }}
            >
              {useRecovery ? 'Usar código do autenticador' : 'Usar código de recuperação'}
            </button>
            <Link to="/login" className="auth-link muted">
              ← Voltar
            </Link>
          </div>
        </>
      }
    >
      <form id="mfa-form" onSubmit={handleSubmit}>
        {useRecovery ? (
          <div className="field">
            <label htmlFor="recovery-code">Código de recuperação</label>
            <input
              id="recovery-code"
              className="input"
              value={recoveryCode}
              onChange={e => setRecoveryCode(e.target.value.toUpperCase())}
              placeholder="A7K2-9MQX"
              disabled={loading}
              style={{ letterSpacing: '0.08em', fontWeight: 600 }}
            />
            <p className="otp-hint" style={{ textAlign: 'left', marginTop: 8 }}>
              Cada código só pode ser usado uma vez.
            </p>
          </div>
        ) : (
          <>
            <OtpInput value={code} onChange={setCode} error={!!error} disabled={loading} />
            <p className="otp-hint">Código do autenticador (TOTP)</p>
          </>
        )}
      </form>
    </AuthCard>
  );
};

export default LoginMfa;
